import React, { useEffect, useRef, useState } from 'react';
import * as echarts from 'echarts';

const EChartLiftCurv = ({ data }) => {
    const chartRef = useRef(null);
    const [theme, setTheme] = useState('light');

    // Detect system theme
    useEffect(() => {
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        const handleChange = () => {
            setTheme(mediaQuery.matches ? 'dark' : 'light');
        };

        handleChange(); // Set initial
        mediaQuery.addEventListener('change', handleChange);

        return () => {
            mediaQuery.removeEventListener('change', handleChange);
        };
    }, []);

    useEffect(() => {
        if (!data || data.length === 0 || !chartRef.current) return;

        let chart = echarts.getInstanceByDom(chartRef.current);
        if (chart) {
            chart.dispose();
        }

        chart = echarts.init(chartRef.current, theme);
        const isDark = theme === 'dark';

        const axisColor = isDark ? '#ccc' : '#333';
        const gridColor = isDark ? '#444' : '#a0aec0';
        const tooltipBg = isDark ? '#2d3748' : 'rgba(255, 255, 255, 0.9)';
        const tooltipText = isDark ? '#eee' : '#333';

        const option = {
            tooltip: {
                trigger: 'axis',
                backgroundColor: tooltipBg,
                borderColor: gridColor,
                borderWidth: 1,
                textStyle: {
                    color: tooltipText,
                }
            },
            legend: {
                data: ['Lift (Total Sales)', 'Cumulative Lift'],
                textStyle: {
                    color: axisColor
                }
            },
            grid: {
                left: '10%',
                right: '10%',
                bottom: '10%',
                top: '10%'
            },
            xAxis: {
                type: 'category',
                name: 'Decile',
                nameLocation: 'middle',
                nameGap: 20,
                data: data.map(item => item.index),
                axisLine: {
                    lineStyle: {
                        color: gridColor
                    }
                },
                axisLabel: {
                    color: axisColor
                },
                nameTextStyle: {
                    color: axisColor
                }
            },
            yAxis: {
                type: 'value',
                name: 'Lift',
                nameRotate: 90,
                nameGap: 40,
                axisLine: {
                    lineStyle: {
                        color: gridColor
                    }
                },
                splitLine: {
                    lineStyle: {
                        color: gridColor,
                        type: 'dashed'
                    }
                },
                axisLabel: {
                    color: axisColor,
                    fontSize: window.innerWidth <= 568 ? 8 : 12,
                },
                nameTextStyle: {
                    color: axisColor
                }
            },
            series: [
                {
                    name: 'Lift (Total Sales)',
                    type: 'line',
                    smooth: true,
                    data: data.map(item => item['Lift (Total Sales)']),
                    lineStyle: {
                        color: '#4A90E2'
                    },
                    itemStyle: {
                        color: '#4A90E2'
                    }
                },
                {
                    name: 'Cumulative Lift',
                    type: 'line',
                    smooth: true,
                    data: data.map(item => item['Cumulative Lift']),
                    lineStyle: {
                        color: '#50E3C2'
                    },
                    itemStyle: {
                        color: '#50E3C2'
                    }
                }
            ]
        };

        chart.setOption(option);

        const resizeObserver = new ResizeObserver(() => chart.resize());
        resizeObserver.observe(chartRef.current);

        return () => {
            resizeObserver.disconnect();
            chart.dispose();
        };
    }, [data, theme]);

    if (!data || data.length === 0) {
        return <p className="text-sm text-gray-500">Lift curve data not available.</p>;
    }

    const containerBg = theme === 'dark' ? '#1e1e1e' : '#ffffff';

    return <div ref={chartRef} style={{ width: '100%', height: '300px', backgroundColor: containerBg }} />;
};

export default EChartLiftCurv;
